import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchUserDetails } from "../../redux/Slices/UsersSlice";
import { fetchCities } from "../../redux/Slices/CitiesSlice";
import { fetchGrades } from "../../redux/Slices/GradesSlice";
import { fetchSections } from "../../redux/Slices/SectionSlice";
import { useTitle } from "../../context/TitleContext";
import { FaArrowRight, FaWallet, FaShoppingCart, FaMoneyBillWave, FaHeadset, FaBell } from "react-icons/fa";

const UserDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { userDetails, isLoading, error } = useSelector((state) => state.users);
  const { cities } = useSelector((state) => state.cities);
  const { grades } = useSelector((state) => state.grades);
  const { data: sections } = useSelector((state) => state.sections);

  // Pagination states
  const [ordersPage, setOrdersPage] = useState(1);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [walletPage, setWalletPage] = useState(1);

  const ITEMS_PER_PAGE = 5;
  const ORDERS_PER_PAGE = 10;

  useEffect(() => {
    setTitle("تفاصيل المستخدم");
    dispatch(fetchUserDetails(id));
    // BUG-37 fix: تحميل قوائم المدن/الصفوف/الأقسام لعرض أسمائها الحقيقية بدلاً من "-"
    dispatch(fetchCities());
    dispatch(fetchGrades());
    dispatch(fetchSections());
  }, [dispatch, id, setTitle]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status"></div>
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="container-fluid text-center mt-5" style={{ direction: "rtl" }}>
        <h4 className="text-danger">{error || "تعذر تحميل تفاصيل المستخدم"}</h4>
        <button className="btn btn-primary mt-3" style={{ borderRadius: 9 }} onClick={() => navigate("/users")}>العودة</button>
      </div>
    );
  }

  const { user, activity } = userDetails;

  // BUG-37 fix: بيانات المستخدم القادمة من الـ API تحتوي فقط على city_id/grade_id/section_id
  // بدون العلاقات المتداخلة، لذلك يجب البحث عن الاسم في القوائم المُحمّلة (نفس منطق EditUser)
  const cityName =
    user?.city?.name_ar ||
    user?.city_name_ar ||
    cities?.data?.find((c) => String(c.id) === String(user?.city_id))?.name_ar ||
    "-";
  const gradeName =
    user?.grade?.name_ar ||
    user?.grade_name_ar ||
    grades?.data?.find((g) => String(g.id) === String(user?.grade_id))?.name_ar ||
    "-";
  const sectionName =
    user?.section?.name_ar ||
    user?.section_name_ar ||
    sections?.find((s) => String(s.id) === String(user?.section_id))?.name_ar ||
    "-";

  const statusOptions = [
    { value: "pending", label: "في الانتظار", color: "var(--warning-color)", bg: "#fef3c7" },
    { value: "in_progress", label: "قيد التنفيذ", color: "#0284c7", bg: "#e0f2fe" },
    { value: "in_way", label: "في الطريق", color: "#4f46e5", bg: "#e0e7ff" },
    { value: "delivered", label: "تم التسليم", color: "#16a34a", bg: "#d1fae5" },
    { value: "cancelled", label: "ملغي", color: "#dc2626", bg: "#fef2f2" },
  ];

  const paymentStatusOptions = [
    { value: "not_paid", label: "غير مدفوع", color: "#dc2626", bg: "#fef2f2" },
    { value: "paid", label: "مدفوع", color: "#16a34a", bg: "#d1fae5" },
  ];

  const getStatusBadge = (status, order) => {
    const isDigital = order?.is_digital || order?.type === "digital" || order?.order_type === "digital" || order?.type === "code" || order?.order_type === "code" || order?.category_name_en?.toLowerCase()?.includes("codes") || order?.items?.some(item => item.product?.category_name_en?.toLowerCase()?.includes("codes"));
    if (isDigital) {
      return (
        <span style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", background: "#d1fae5", padding: "3px 10px", borderRadius: 20 }}>
          طلب رقمي (تم التسليم)
        </span>
      );
    }
    const statusOption = statusOptions.find(opt => opt.value === status);
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: statusOption?.color || "#475569", background: statusOption?.bg || "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
        {statusOption?.label || status}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusOption = paymentStatusOptions.find(opt => opt.value === status);
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: statusOption?.color || "#475569", background: statusOption?.bg || "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
        {statusOption?.label || status}
      </span>
    );
  };

  const paginate = (items, pageNumber, pageSize) => {
    if (!items) return [];
    const startIndex = (pageNumber - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
  };

  const SimplePagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-center align-items-center gap-2 mt-3 mb-2">
        <button 
          onClick={() => onPageChange(currentPage - 1)} 
          disabled={currentPage === 1}
          style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: currentPage === 1 ? "#cbd5e1" : "#475569", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
        >
          السابق
        </button>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
          {currentPage} من {totalPages}
        </span>
        <button 
          onClick={() => onPageChange(currentPage + 1)} 
          disabled={currentPage === totalPages}
          style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: currentPage === totalPages ? "#cbd5e1" : "#475569", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
        >
          التالي
        </button>
      </div>
    );
  };

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .details-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .details-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 12px 14px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .details-table td {
          padding: 12px 14px !important;
          font-size: 13px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            👤 تفاصيل حساب الطالب: {user?.name}
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>عرض تقرير سجلات النشاط، الطلبات، وحالة المحفظة الخاصة بالطالب</p>
        </div>
        <button onClick={() => navigate("/users")} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
          <FaArrowRight size={12} style={{ marginLeft: 4 }} /> عودة للقائمة
        </button>
      </div>

      <div className="row">
        {/* User Profile Card */}
        <div className="col-lg-4 mb-4">
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
            <div style={{ padding: "24px 16px", textAlign: "center", borderBottom: "1px solid #f1f5f9" }}>
              <img 
                src={user?.image || "/user.webp"} 
                alt={user?.name} 
                onError={(e) => (e.target.src = "/user.webp")}
                style={{ width: "110px", height: "110px", objectFit: "cover", borderRadius: "50%", border: "3px solid #fff", boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }}
                className="mb-3"
              />
              <h5 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>{user?.name}</h5>
              <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>{user?.phone}</p>
              {user?.parent_phone && <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 12 }}>هاتف ولي الأمر: {user?.parent_phone}</p>}
              <div style={{ marginTop: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: (user?.status === "active" || user?.status === true) ? "#15803d" : "#64748b", background: (user?.status === "active" || user?.status === true) ? "#d1fae5" : "#f1f5f9", padding: "4px 12px", borderRadius: 20 }}>
                  {(user?.status === "active" || user?.status === true) ? "مفعل" : "غير مفعل"}
                </span>
              </div>
            </div>
            <div style={{ padding: "16px 20px", background: "#f8fafc" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e2e8f0", fontSize: 13 }}>
                <span style={{ color: "#64748b", fontWeight: 500 }}>المدينة</span>
                <strong style={{ color: "#0f172a" }}>{cityName}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e2e8f0", fontSize: 13 }}>
                <span style={{ color: "#64748b", fontWeight: 500 }}>الصف</span>
                <strong style={{ color: "#0f172a" }}>{gradeName}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e2e8f0", fontSize: 13 }}>
                <span style={{ color: "#64748b", fontWeight: 500 }}>القسم</span>
                <strong style={{ color: "#0f172a" }}>{sectionName}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", fontSize: 13 }}>
                <span style={{ color: "#64748b", fontWeight: 500 }}>تاريخ التسجيل</span>
                <strong style={{ color: "#0f172a" }}>{user?.created_at ? new Date(user.created_at).toLocaleDateString('ar-EG') : "-"}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Summary Stats */}
        <div className="col-lg-8 mb-4">
          <div className="row">
            <div className="col-md-6 mb-4">
              <div style={{ background: "linear-gradient(135deg,#3b82f6,#1d4ed8)", borderRadius: 14, color: "#fff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 4px 12px rgba(37,99,235,0.15)" }}>
                <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "50%", padding: 12, display: "flex" }}>
                  <FaWallet size={22} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.8)" }}>رصيد المحفظة</div>
                  <h3 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>{user?.wallet_balance || 0} ج.م</h3>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div style={{ background: "linear-gradient(135deg,var(--green-color),#047857)", borderRadius: 14, color: "#fff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 4px 12px rgba(16,185,129,0.15)" }}>
                <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "50%", padding: 12, display: "flex" }}>
                  <FaShoppingCart size={22} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.8)" }}>إجمالي الطلبات</div>
                  <h3 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>{activity?.orders_count || 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div style={{ background: "linear-gradient(135deg,#06b6d4,#0891b2)", borderRadius: 14, color: "#fff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 4px 12px rgba(6,182,212,0.15)" }}>
                <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "50%", padding: 12, display: "flex" }}>
                  <FaMoneyBillWave size={22} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.8)" }}>عمليات المحفظة</div>
                  <h3 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>{activity?.wallet_transactions?.length || 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div style={{ background: "linear-gradient(135deg,#f59e0b,var(--warning-color))", borderRadius: 14, color: "#fff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 4px 12px rgba(245,158,11,0.15)" }}>
                <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "50%", padding: 12, display: "flex" }}>
                  <FaHeadset size={22} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.8)" }}>تذاكر الدعم الفني</div>
                  <h3 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>{activity?.contacts?.length || 0}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Detailed Tables ───────────────────────────── */}
      <div className="row">
        {/* Orders Table */}
        <div className="col-12 mb-4">
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
              <FaShoppingCart style={{ color: "var(--main-color)" }} />
              <span style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>سجل الطلبات الأكاديمية والكتب</span>
            </div>
            <div style={{ padding: 0 }}>
              <div className="table-responsive">
                <table className="details-table">
                  <thead>
                    <tr>
                      <th>رقم الطلب</th>
                      <th>نوع الطلب</th>
                      <th>التاريخ</th>
                      <th>المجموع الإجمالي</th>
                      <th>حالة الدفع</th>
                      <th>حالة الطلب</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity?.orders?.length > 0 ? (
                      paginate(activity.orders, ordersPage, ORDERS_PER_PAGE).map((order) => (
                        <tr key={order.id}>
                          <td style={{ fontWeight: 700, color: "#0f172a" }}>{order.order_number}</td>
                          <td>
                            {order.order_type === 'code' || order.type === 'code' || order.type === 'digital' || order.order_type === 'digital' ? (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#4f46e5", background: "#e0e7ff", padding: "3px 10px", borderRadius: 20 }}>🔑 كود رقمي</span>
                            ) : order.order_type === 'book' || order.type === 'book' ? (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--warning-color)", background: "#fef3c7", padding: "3px 10px", borderRadius: 20 }}>📚 مطبوعات ورقية</span>
                            ) : (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>—</span>
                            )}
                          </td>
                          <td>{new Date(order.created_at).toLocaleDateString('ar-EG')}</td>
                          <td>
                            <strong style={{ color: "#16a34a" }}>{order.total_after_discount || order.grand_total} ج.م</strong>
                            {order.total_before_discount !== order.total_after_discount && order.total_before_discount && (
                              <div style={{ fontSize: 11, textDecoration: "line-through", color: "#94a3b8" }}>{order.total_before_discount} ج.م</div>
                            )}
                          </td>
                          <td>{getPaymentStatusBadge(order.payment_status)}</td>
                          <td>{getStatusBadge(order.order_status || order.status, order)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="6" style={{ padding: 24, color: "#94a3b8" }}>لا توجد طلبات مسجلة</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {activity?.orders && (
                <div style={{ padding: "0 16px 16px" }}>
                  <SimplePagination 
                    totalItems={activity.orders.length} 
                    itemsPerPage={ORDERS_PER_PAGE} 
                    currentPage={ordersPage} 
                    onPageChange={setOrdersPage} 
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notifications Listing */}
        <div className="col-md-6 mb-4">
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
              <FaBell style={{ color: "var(--red-color)" }} />
              <span style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>الإشعارات المستلمة</span>
            </div>
            <div style={{ flex: 1, padding: 0 }}>
              <ul className="list-group list-group-flush" style={{ padding: 0, margin: 0 }}>
                {activity?.notifications?.length > 0 ? (
                  paginate(activity.notifications, notificationsPage, ITEMS_PER_PAGE).map((notif) => (
                    <li key={notif.id} className="list-group-item" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <h6 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{notif.title_ar || notif.title}</h6>
                        <small style={{ color: "#94a3b8" }}>{new Date(notif.created_at).toLocaleDateString('ar-EG')}</small>
                      </div>
                      <p style={{ margin: 0, color: "#64748b", fontSize: 12 }}>{notif.body_ar || notif.body}</p>
                      <div style={{ marginTop: 6 }}>
                        {notif.fcm_status === 'sent' ? (
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", background: "#d1fae5", padding: "2px 8px", borderRadius: 20 }}>تم الإرسال بنجاح</span>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "2px 8px", borderRadius: 20 }}>مجدول أو معلق</span>
                        )}
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="list-group-item text-center py-5" style={{ color: "#94a3b8" }}>لا توجد إشعارات مستلمة</li>
                )}
              </ul>
              {activity?.notifications && (
                <div style={{ padding: "16px" }}>
                  <SimplePagination 
                    totalItems={activity.notifications.length} 
                    itemsPerPage={ITEMS_PER_PAGE} 
                    currentPage={notificationsPage} 
                    onPageChange={setNotificationsPage} 
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Wallet Transactions Listing */}
        <div className="col-md-6 mb-4">
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
              <FaWallet style={{ color: "var(--green-color)" }} />
              <span style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>آخر حركات المحفظة</span>
            </div>
            <div style={{ flex: 1, padding: 0 }}>
              <ul className="list-group list-group-flush" style={{ padding: 0, margin: 0 }}>
                {activity?.wallet_transactions?.length > 0 ? (
                  paginate(activity.wallet_transactions, walletPage, ITEMS_PER_PAGE).map((trans) => (
                    <li key={trans.id} className="list-group-item" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <h6 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{trans.type === 'credit' ? 'إيداع رصيد' : 'سحب رصيد'}</h6>
                        <small style={{ color: "#94a3b8" }}>{new Date(trans.created_at).toLocaleDateString('ar-EG')}</small>
                        {trans.notes && (
                          <small style={{ display: "block", color: "#64748b", fontSize: 11, marginTop: 4 }}>
                            📝 {trans.notes}
                          </small>
                        )}
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: trans.type === 'credit' ? "#16a34a" : "#dc2626" }}>
                          {trans.type === 'credit' ? '+' : '-'}{trans.amount} ج.م
                        </span>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>الرصيد بعد: {trans.balance_after} ج.م</div>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="list-group-item text-center py-5" style={{ color: "#94a3b8" }}>لا توجد حركات سابقة بالمحفظة</li>
                )}
              </ul>
              {activity?.wallet_transactions && (
                <div style={{ padding: "16px" }}>
                  <SimplePagination 
                    totalItems={activity.wallet_transactions.length} 
                    itemsPerPage={ITEMS_PER_PAGE} 
                    currentPage={walletPage} 
                    onPageChange={setWalletPage} 
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
